import { LitElement, css, html } from "lit";

import { customElement, property, state } from "lit/decorators.js";

import type { FileContext } from "api-contracts/rpc/getFileUploadUrl";
import { FileUploadError, formatUploadError, uploadFile } from "ui-common/services/file-upload";

// Translation function removed - using configurable text properties instead

@customElement("file-upload")
export class FileUpload extends LitElement {
  @property({ type: String })
  accept = "image/*,video/*";

  @property({ type: String })
  label = "Upload File";

  @property({ type: String })
  context: FileContext = "brand_asset";

  @property({ type: String })
  uploadingText = "Uploading...";

  @property({ type: String })
  dragAndDropText = "or drag and drop files here";

  @property({ type: String })
  uploadSuccessText = "Upload successful!";

  @property({ type: String })
  uploadFailedText = "Upload failed";

  @state()
  private uploading = false;

  @state()
  private uploadProgress = 0;

  @state()
  private uploadedUrl?: string;

  @state()
  private error?: string;

  @state()
  private isDragging = false;

  static override styles = css`
    :host {
      display: block;
      width: 100%;
    }

    .upload-container {
      border: 2px dashed var(--sds-color-border-default-secondary);
      border-radius: var(--sds-size-radius-600);
      padding: var(--sds-size-space-800);
      text-align: center;
      cursor: pointer;
      transition: all 0.2s ease;
      background: var(--sds-color-background-default-default);
    }

    .upload-container:hover {
      border-color: var(--sds-color-border-brand-secondary);
      background: var(--sds-color-background-brand-tertiary);
    }

    .upload-container.dragging {
      border-color: var(--sds-color-border-brand-primary);
      background: var(--sds-color-background-brand-secondary);
    }

    .upload-input {
      display: none;
    }

    .upload-button {
      background: var(--sds-color-background-brand-secondary);
      color: var(--sds-color-text-neutral-inverse);
      border: none;
      padding: var(--sds-size-space-400) var(--sds-size-space-600);
      border-radius: var(--sds-size-radius-400);
      cursor: pointer;
      font-size: var(--sds-typography-body-size-default);
      font-family: var(--sds-typography-family-default);
      font-weight: var(--sds-typography-weight-medium);
      transition: background-color 0.2s ease;
    }

    .upload-button:hover {
      background: var(--sds-color-background-brand-primary);
    }

    .upload-button:disabled {
      background: var(--sds-color-background-neutral-secondary);
      color: var(--sds-color-text-default-tertiary);
      cursor: not-allowed;
    }

    .upload-text {
      margin-top: var(--sds-size-space-400);
      font-size: var(--sds-typography-body-size-small);
      color: var(--sds-color-text-default-secondary);
      font-family: var(--sds-typography-family-default);
    }

    .progress-bar {
      width: 100%;
      height: 8px;
      background: var(--sds-color-background-neutral-tertiary);
      border-radius: var(--sds-size-radius-200);
      margin: var(--sds-size-space-400) 0;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      background: var(--sds-color-background-brand-secondary);
      transition: width 0.3s ease;
    }

    .progress-text {
      font-size: var(--sds-typography-body-size-small);
      color: var(--sds-color-text-default-secondary);
      margin-top: var(--sds-size-space-200);
    }

    .success {
      color: var(--sds-color-text-success-primary);
      margin-top: var(--sds-size-space-400);
      font-size: var(--sds-typography-body-size-small);
    }

    .error {
      color: var(--sds-color-text-danger-secondary);
      margin-top: var(--sds-size-space-400);
      font-size: var(--sds-typography-body-size-small);
    }

    .file-info {
      margin-top: var(--sds-size-space-300);
      font-size: var(--sds-typography-body-size-small);
      color: var(--sds-color-text-default-tertiary);
      word-break: break-all;
    }
  `;

  private async handleFileSelect(file: File) {
    if (!file) return;

    this.error = undefined;
    this.uploadedUrl = undefined;
    this.uploading = true;
    this.uploadProgress = 0;

    try {
      // Use the consolidated upload service
      const result = await uploadFile(file, this.context, {
        onProgress: (progress) => {
          this.uploadProgress = progress;
        },
      });

      this.uploadedUrl = result.url;

      this.dispatchEvent(
        new CustomEvent("file-uploaded", {
          detail: {
            file,
            url: result.url,
            relativePath: result.url,
            fileId: result.fileId,
          },
          bubbles: true,
        }),
      );
    } catch (error) {
      console.error("File upload error:", error);
      if (error instanceof FileUploadError) {
        this.error = formatUploadError(error);
      } else {
        this.error = error instanceof Error ? error.message : this.uploadFailedText;
      }
    } finally {
      this.uploading = false;
      this.uploadProgress = 0;
    }
  }

  private handleFileInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.handleFileSelect(file);
    }
  }

  private handleDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files[0];
    if (file) {
      this.handleFileSelect(file);
    }
  }

  private handleDragOver(event: DragEvent) {
    event.preventDefault();
  }

  private handleDragEnter(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  private handleDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  override render() {
    return html`
      <div
        class="upload-container ${this.isDragging ? "dragging" : ""}"
        @drop=${this.handleDrop}
        @dragover=${this.handleDragOver}
        @dragenter=${this.handleDragEnter}
        @dragleave=${this.handleDragLeave}
        @click=${() => this.shadowRoot?.querySelector<HTMLInputElement>(".upload-input")?.click()}
      >
        <input
          class="upload-input"
          type="file"
          accept=${this.accept}
          @change=${this.handleFileInput}
          ?disabled=${this.uploading}
        />

        ${this.uploading
          ? html`
              <div>
                <p>${this.uploadingText}</p>
                <div class="progress-bar">
                  <div class="progress-fill" style="width: ${this.uploadProgress}%"></div>
                </div>
                <div class="progress-text">${Math.round(this.uploadProgress)}%</div>
              </div>
            `
          : html`
              <div>
                <button class="upload-button" ?disabled=${this.uploading}>${this.label}</button>
                <p class="upload-text">${this.dragAndDropText}</p>
              </div>
            `}
        ${this.uploadedUrl
          ? html`
              <div class="success">
                <p>${this.uploadSuccessText}</p>
              </div>
            `
          : ""}
        ${this.error
          ? html`
              <div class="error">
                <p>❌ ${this.error}</p>
              </div>
            `
          : ""}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "file-upload": FileUpload;
  }
}
